import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FolderOpen, Upload, FileText, Image, File, Trash2, Download, ExternalLink,
  Copy, Search, Tag, Eye, Edit, X, Loader2, HardDrive, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { getContentLibrary, uploadContentFile, updateContentAsset, deleteContentAsset } from '../lib/api';

const CATEGORIES = [
  { value: 'all', label: 'All Files', icon: FolderOpen },
  { value: 'report', label: 'Reports', icon: FileText },
  { value: 'image', label: 'Images', icon: Image },
  { value: 'template', label: 'Templates', icon: File },
  { value: 'document', label: 'Documents', icon: FileText },
  { value: 'presentation', label: 'Presentations', icon: BarChart3 },
];

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(contentType) {
  if (!contentType) return File;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.includes('pdf')) return FileText;
  if (contentType.includes('presentation') || contentType.includes('pptx')) return BarChart3;
  return FileText;
}

function getCategoryFromType(contentType) {
  if (!contentType) return 'document';
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.includes('pdf')) return 'report';
  if (contentType.includes('presentation') || contentType.includes('pptx')) return 'presentation';
  return 'document';
}

export default function ContentLibrary() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', category: '', tags: '' });
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'document', tags: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const loadAssets = useCallback(async () => {
    try {
      const res = await getContentLibrary(selectedCategory === 'all' ? '' : selectedCategory);
      setAssets(res.data || []);
    } catch (err) {
      console.error('Failed to load content library:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    setUploading(true);
    try {
      const meta = {
        title: uploadForm.title || selectedFile.name,
        description: uploadForm.description,
        category: uploadForm.category || getCategoryFromType(selectedFile.type),
        tags: uploadForm.tags,
      };
      await uploadContentFile(selectedFile, meta);
      toast.success('File uploaded successfully');
      setShowUpload(false);
      setSelectedFile(null);
      setUploadForm({ title: '', description: '', category: 'document', tags: '' });
      loadAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteContentAsset(id);
      toast.success('File deleted');
      setAssets(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleUpdate = async () => {
    if (!showEdit) return;
    try {
      const tagList = editForm.tags
        ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      await updateContentAsset(showEdit.id, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        tags: tagList,
      });
      toast.success('Asset updated');
      setShowEdit(null);
      loadAssets();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setUploadForm(prev => ({
        ...prev,
        title: prev.title || e.dataTransfer.files[0].name,
        category: getCategoryFromType(e.dataTransfer.files[0].type),
      }));
      setShowUpload(true);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const filteredAssets = assets.filter(a => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (a.title || '').toLowerCase().includes(q) ||
        (a.filename || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q) ||
        (a.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  const totalSize = assets.reduce((sum, a) => sum + (a.size || 0), 0);

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="content-library-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0">
              <HardDrive className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Asset Management</p>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="content-library-title">Content Library</h1>
              <p className="text-sm text-white/50 mt-0.5">Upload, manage, and distribute reports, images, and documents</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Files</p>
              <p className="text-xl font-bold text-[hsl(var(--brand))] font-mono">{assets.length}</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Storage</p>
              <p className="text-xl font-bold text-white font-mono">{formatFileSize(totalSize)}</p>
            </div>
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press"
              data-testid="upload-file-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Upload File
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex items-center gap-4 mb-6 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="flex-shrink-0">
          <TabsList className="bg-[hsl(var(--pane))] border border-white/10">
            {CATEGORIES.map(cat => (
              <TabsTrigger key={cat.value} value={cat.value} className="text-xs gap-1.5" data-testid={`content-filter-${cat.value}`}>
                <cat.icon className="w-3.5 h-3.5" />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search files by name, tag, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[hsl(var(--pane))] border-white/10"
            data-testid="content-search-input"
          />
        </div>
      </div>

      {/* Drop Zone + Asset Grid */}
      <div
        className={`animate-fade-in-up`}
        style={{ animationDelay: '0.1s' }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {dragActive && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center" data-testid="drop-overlay">
            <div className="p-12 rounded-2xl border-2 border-dashed border-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.08)] text-center">
              <Upload className="w-16 h-16 mx-auto mb-4 text-[hsl(var(--brand))]" />
              <p className="text-xl font-semibold text-foreground">Drop your file here</p>
              <p className="text-sm text-muted-foreground mt-1">Supports PDFs, images, documents up to 10MB</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--brand))]" />
          </div>
        ) : filteredAssets.length === 0 ? (
          <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{ boxShadow: 'var(--shadow-elev-1)' }}>
            <CardContent className="text-center py-16">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <p className="text-lg font-medium text-muted-foreground mb-2">
                {searchQuery ? 'No files match your search' : 'No files uploaded yet'}
              </p>
              <p className="text-sm text-muted-foreground/70 mb-6">
                Upload reports, images, templates, and documents to your content library
              </p>
              <Button onClick={() => setShowUpload(true)} className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press">
                <Upload className="w-4 h-4 mr-2" /> Upload Your First File
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-grid">
            {filteredAssets.map(asset => {
              const FileIcon = getFileIcon(asset.content_type);
              const isImage = (asset.content_type || '').startsWith('image/');
              return (
                <Card
                  key={asset.id}
                  className="bg-[hsl(var(--pane))] border border-white/10 card-hover-lift group"
                  style={{ boxShadow: 'var(--shadow-elev-1)' }}
                  data-testid={`content-card-${asset.id}`}
                >
                  <CardContent className="p-0">
                    {/* Preview area */}
                    <div className="relative h-36 bg-[hsl(var(--secondary)/0.3)] flex items-center justify-center overflow-hidden rounded-t-2xl">
                      {isImage && asset.file_url ? (
                        <img
                          src={asset.file_url}
                          alt={asset.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FileIcon className="w-12 h-12 text-muted-foreground/30" />
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        {asset.file_url && (
                          <>
                            <a href={asset.file_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="secondary" size="sm" className="bg-white/20 backdrop-blur border-0 text-white hover:bg-white/30 btn-press">
                                <Eye className="w-3.5 h-3.5 mr-1" /> View
                              </Button>
                            </a>
                            <Button variant="secondary" size="sm" className="bg-white/20 backdrop-blur border-0 text-white hover:bg-white/30 btn-press" onClick={() => copyUrl(asset.file_url)}>
                              <Copy className="w-3.5 h-3.5 mr-1" /> URL
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-1.5">
                        <h3 className="text-sm font-medium truncate flex-1 mr-2" data-testid={`content-title-${asset.id}`}>
                          {asset.title}
                        </h3>
                        <Badge className="bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))] border border-[hsl(var(--brand)/0.2)] text-[10px] flex-shrink-0">
                          {asset.category || 'file'}
                        </Badge>
                      </div>
                      {asset.description && (
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{asset.description}</p>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70 mb-3 font-mono">
                        <span>{formatFileSize(asset.size)}</span>
                        <span className="text-white/10">|</span>
                        <span>{asset.download_count || 0} downloads</span>
                        <span className="text-white/10">|</span>
                        <span>{new Date(asset.created_at).toLocaleDateString()}</span>
                      </div>
                      {(asset.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {asset.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="inline-flex items-center gap-0.5 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[10px] text-muted-foreground">
                              <Tag className="w-2.5 h-2.5" /> {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs btn-press"
                          onClick={() => {
                            setShowEdit(asset);
                            setEditForm({
                              title: asset.title || '',
                              description: asset.description || '',
                              category: asset.category || 'document',
                              tags: (asset.tags || []).join(', '),
                            });
                          }}
                          data-testid={`edit-asset-${asset.id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <a href={asset.file_url} download>
                          <Button variant="outline" size="sm" className="text-xs btn-press" data-testid={`download-asset-${asset.id}`}>
                            <Download className="w-3 h-3" />
                          </Button>
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs btn-press"
                          onClick={() => handleDelete(asset.id)}
                          data-testid={`delete-asset-${asset.id}`}
                        >
                          <Trash2 className="w-3 h-3 text-[hsl(var(--status-danger))]" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>Add a file to your content library. Supports PDFs, images, and documents up to 10MB.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* File picker */}
            <div
              className={`p-6 rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors duration-150 ${
                selectedFile
                  ? 'border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.05)]'
                  : 'border-white/15 hover:border-white/25 bg-white/[0.02]'
              }`}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.svg,.pptx,.xlsx,.csv,.md,.txt"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    setSelectedFile(e.target.files[0]);
                    setUploadForm(prev => ({
                      ...prev,
                      title: prev.title || e.target.files[0].name,
                      category: getCategoryFromType(e.target.files[0].type),
                    }));
                  }
                }}
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-[hsl(var(--brand))]" />
                  <div className="text-left">
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">Click to browse or drag & drop</p>
                  <p className="text-xs text-muted-foreground/50 mt-1">PDF, DOC, PNG, JPG, PPTX, CSV, MD (max 10MB)</p>
                </>
              )}
            </div>

            <div>
              <Label>Title</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Q4 Threat Report 2026"
                data-testid="upload-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this asset..."
                rows={2}
                data-testid="upload-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={uploadForm.category} onValueChange={(v) => setUploadForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="report">Report</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="template">Template</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="presentation">Presentation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags</Label>
                <Input
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="security, Q4, threat"
                  data-testid="upload-tags-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press"
              data-testid="upload-submit-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-asset-dialog">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
            <DialogDescription>Update the metadata for this file.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))} data-testid="edit-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} rows={2} data-testid="edit-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="report">Report</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="template">Template</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="presentation">Presentation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags</Label>
                <Input value={editForm.tags} onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))} placeholder="tag1, tag2" data-testid="edit-tags-input" />
              </div>
            </div>
            {showEdit?.file_url && (
              <div>
                <Label>Public URL</Label>
                <div className="flex gap-2">
                  <Input value={showEdit.file_url} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copyUrl(showEdit.file_url)}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(null)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press" data-testid="edit-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
